import { yupResolver } from '@hookform/resolvers/yup';
import type { Gender } from '@kimuno/api-sdk';
import { useContext, useEffect, useRef, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateMe as updateMeAction } from '../../actions/user.actions';
import { selectUser } from '../../reducers/user.selectors';
import { NotificationContext } from '../../state/NotificationContextProvider';
import useAction from '../../utils/useAction';
import EditableSection from '../EditableSection';
import BillingAddressForm, { type ChangeBillingAddressPayload } from './BillingAddressForm';
import BillingAddressView from './BillingAddressView';
import { getBillingAddressFields } from './billingAddressFields';
import { billingAddressSchema } from './billingAddressSchema';

const BillingAddressInfo = () => {
	const { t } = useTranslation();
	const updateMe = useAction(updateMeAction);
	const { showNotification } = useContext(NotificationContext);
	const user = useSelector(selectUser);
	const originalValuesRef = useRef<ChangeBillingAddressPayload | null>(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const fields = getBillingAddressFields(user);

	// SUB-510: `isSchoolUser` only recognises Zuora group prefixes, so a kimUno
	// school's seat holders (billed through the school) are caught by `groupId`.
	const isBusinessUser =
		!!user?.isSchoolUser || !!user?.groupId || !!user?.isLibraryUser || !!user?.isKeycloakSSO;
	const showInvoiceInformation = !user?.isReadOnlyLicence && !isBusinessUser;

	const {
		register,
		formState: { errors },
		control,
		getValues,
		trigger,
		reset,
	} = useForm<ChangeBillingAddressPayload>({
		mode: 'onTouched',
		shouldUnregister: true,
		resolver: yupResolver(billingAddressSchema) as Resolver<ChangeBillingAddressPayload>,
		defaultValues: fields.reduce((acc, field) => {
			acc[field.name] = field.value || '';
			return acc;
		}, {} as ChangeBillingAddressPayload),
	});

	const onSaveChanges = async ({ toggleEditMode }: { toggleEditMode: () => void }) => {
		const isValid = await trigger([
			'firstName',
			'lastName',
			'street',
			'postalCode',
			'city',
			'country',
			'institution',
		]);

		if (!isValid) return;

		if (!user) return;

		const formData = getValues();

		try {
			await updateMe({
				invoiceAddress: {
					firstName: formData.firstName,
					lastName: formData.lastName,
					street: formData.street,
					areaCode: formData.postalCode,
					city: formData.city,
					country: formData.country,
					institution: formData.institution || '',
					gender: user?.invoiceAddress.gender as Gender,
				},
			});
			toggleEditMode();
			showNotification({
				message: t('All changes were saved successfully'),
				severity: 'success',
			});
		} catch {
			showNotification({
				message: t('An error occurred while saving changes'),
				severity: 'error',
			});
		}
	};

	useEffect(() => {
		if (!user) return;

		const newFields = getBillingAddressFields(user);
		const newValues = newFields.reduce((acc, field) => {
			acc[field.name] = field.value || '';
			return acc;
		}, {} as ChangeBillingAddressPayload);

		originalValuesRef.current = newValues;
		reset(newValues);
	}, [user, reset]);

	if (!showInvoiceInformation) return null;

	return (
		<EditableSection
			title={t('Billing address')}
			onSaveChanges={onSaveChanges}
			dataTestId="billing-address-section"
			isEditMode={isEditMode}
			onEditModeChange={setIsEditMode}
		>
			{({ isEditMode }) =>
				isEditMode ? (
					<BillingAddressForm errors={errors} fields={fields} register={register} control={control} />
				) : (
					<BillingAddressView fields={fields} />
				)
			}
		</EditableSection>
	);
};

export default BillingAddressInfo;
